import { Box, Grid, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useHistory } from 'react-router-dom'

//component
import HeaderCard from 'component/CardCustom/HeaderCard'

//utils
import { numberFormat, toBuddhistYear } from 'utils/app.utils'

/** STYLE */

//API
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import OperativeTypeApi from 'api/setting/treatments/operative-type.api'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import { SORT_TYPE } from 'constants/common'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import ExportToExcel from './ExportToExcel'
import ProfitSummaryList from './ProfitSummaryList'

export default function ProfitSummary() {
    const { t } = useTranslation()
    const history = useHistory();
    const momentDate = () => [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]

    const componentRef = useRef<HTMLDivElement>(null)
    const [branches, setBranches] = useState<any[]>([])
    const [operativeType, setOperativeType] = useState<any[]>([])
    const [loading, setLoading] = useState<boolean>(true);

    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState<number>(10)
    const [search, setSearch] = useState<string>('')

    const [branchId, setBranchId] = useState<any>(0)
    const [operativeId, setOperativeId] = useState<any>(null)
    const [operativeTypeId, setOperativeTypeId] = useState<any>(null)

    const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
    const [profitSummaryData, setProfitSummaryData] = useState<any>([]);
    const [profitSummaryExportData, setProfitSummaryExportData] = useState<any>([]);
    const [fetch, setFetch] = useState<boolean>(false)

    const [rowCount, setRowCount] = useState<number>(0);
    const [sortType, setSortType] = useState<SORT_TYPE>(SORT_TYPE.DESC)
    const [sortBy, setSortBy] = useState<string>('order_treatments.orderTreatmentId')

    const getAllFilter = () => {
        return [{
            label: t('REPORT.FILTER.BRANCH'),
            value: branchId !== 0 ? _.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn') : t('REPORT.REPORT_GROUP.ALL')
        }, {
            label: t('REPORT.FILTER.TYPE_OPERATION'),
            value: operativeTypeId ? _.get(_.find(operativeType, { operativeTypeId: operativeTypeId }), 'operativeTypeName') || _.get(_.find(operativeType, { operativeTypeId: operativeTypeId }), 'operativeTypeNameEn') : t('REPORT.REPORT_GROUP.ALL')
        },
        {
            label: t('REPORT.FILTER.START'),
            value: rangeDate[0] ? toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY') : '-'
        },
        {
            label: t('REPORT.FILTER.DATE_END'),
            value: rangeDate[0] ? toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY') : '-'
        }

        ]

    }



    const fetchProfitSummary = useCallback(() => {
        setLoading(true)
        let condition: FindAllReportInterface = {
            page: page, pageLimit: limit,
            dateStart: rangeDate[0],
            dateEnd: rangeDate[1],
        }

        console.log(branchId, 'branchId')
        if (branchId && branchId !== 0) {
            condition = { ...condition, branchId }
        }
        if (rangeDate) {
            if (rangeDate[0] !== null && rangeDate[1] !== null)
                condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }
        }
        if (operativeTypeId) {
            condition.operativeTypeId = operativeTypeId
        }
        if (!_.isEmpty(search)) {
            condition.search = search
        }

        ReportApi.reportProfitSummary(condition)
            .then(({ headers, data }) => {
                setProfitSummaryData(data)
                setRowCount(headers['x-total'])
            }).catch((e) => setLoading(false))
            .finally(() => {
                setLoading(false)
                setFetch(false)
            })

    }, [page, limit, fetch, sortType, sortBy])

    const verifyPermission = async () => {
        ClinicApi.findAllBranches().then(({ data }) => { setBranches(data); }).catch((e) => { return; });
        OperativeTypeApi.findAll({ export: 1 }).then(({ data }) => {
            setOperativeType([{ operativeTypeName: t('REPORT.FILTER.TYPE_OPERATION_ALL'), operativeTypeId: 0 }, ...data])
        }).catch((e) => { return })

        return true
    }

    useEffect(() => {
        verifyPermission()
    }, [])

    useEffect(() => {
        fetchProfitSummary()
    }, [fetchProfitSummary])


    useEffect(() => {
        setOperativeId(null)
    }, [operativeTypeId])

    const handleChangeRowsPerPage = (limit: number) => {
        setLimit(limit)
    }

    const handleChangePage = (val: number) => setPage(val)

    const handleExport = async (format: 'Excel' | 'PDF') => {
        setLoading(true);
        let condition: FindAllReportInterface = {
            page: 1,
            pageLimit: -1,
            dateStart: rangeDate[0],
            dateEnd: rangeDate[1],
            export: 1
        }

        if (branchId && branchId !== 0) {
            condition = { ...condition, branchId }
        }

        if (operativeTypeId) {
            condition.operativeTypeId = operativeTypeId
        }
        if (!_.isEmpty(search)) {
            condition.search = search
        }

        try {
            const { data } = await ReportApi.reportProfitSummary(condition)
            setProfitSummaryExportData(data)

            if (format === 'Excel') {
                await ExportToExcel({
                    title: t('REPORT.REPORT_NAME.PROFIT_SUMMARY'),
                    data: data,
                    branchName: `${getAllFilter()[0].value}`,
                    dateStart: `${getAllFilter()[2].value}`,
                    dateEnd: `${getAllFilter()[3].value}`,
                    operative: `${getAllFilter()[1].label}`,
                    t: t,
                });
            } else if (format === 'PDF') {
                setTimeout(() => {
                    const style = document.createElement("style");
                    style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `;
                    document.head.appendChild(style);
                    window.print();
                    document.head.removeChild(style);
                }, 100)
            }

        } catch (error) {
            console.error('Error exporting:', error);
        } finally {
            setLoading(false);
        }
    }

    console.log(getAllFilter())
    return (
        <Box>
            <HeaderCard text={t(`REPORT.REPORT_NAME.PROFIT_SUMMARY`)} />
            <Box sx={{ px: 3, pt: 2 }}>
                <Grid container spacing={2} >
                    <Grid xs={12} sm={6} item md={3} xl={2}>
                        <SelectInput
                            label={t('REPORT.FILTER.BRANCH')}
                            value={branchId}
                            onChange={e => {
                                setBranchId(Number(e.target.value));
                            }}
                            options={[
                                { value: 0, name: t(`REPORT.FILTER.ALL`) },
                                ...branches.map(b => ({
                                    value: b.branchId,
                                    name: b.branchName || b.branchNameEn || ''
                                }))]}
                            width="100%"
                        />
                    </Grid>
                    <Grid xs={12} sm={6} item md={3} xl={2}>
                        <InputSecondNewRangePicker
                            inputHeight={40}
                            value={rangeDate || emptyRangePicker}
                            onchange={val => {
                                if (_.isEmpty(val)) setRangeDate(emptyRangePicker);
                                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')]);
                            }}
                            label=""
                        />
                    </Grid>
                    <Grid xs={12} sm={6} item md={3} xl={2}>
                        <SelectInput
                            label={t('REPORT.TABLE.FOLLOW_UP.OPERATIVE_TYPE')}
                            value={operativeTypeId || 0}
                            onChange={e => setOperativeTypeId(e.target.value)}
                            options={operativeType.map(b => ({
                                value: b.operativeTypeId,
                                name: b.operativeTypeName,
                            }))}
                            width="100%"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={6}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <MainButton
                                title={t('INPUT.SEARCH')}
                                type="primary"
                                variant="contained"
                                onClick={() => {
                                    setFetch(true);
                                    // setPage(1);
                                }}
                                style={{ width: '120px' }}
                            />
                            <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                                if (e === 'Excel') {
                                    handleExport('Excel')
                                } else if (e === 'PDF') {
                                    handleExport('PDF')
                                }
                            }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                        </Box>
                    </Grid>
                </Grid>
                <Box className={'mt-3 pb-4'}>
                    <ProfitSummaryList
                        page={page}
                        pageLimit={limit}
                        rowCount={rowCount}
                        data={profitSummaryData}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        handleChangePage={handleChangePage}
                        onRequestSort={() => { return }}
                        sortBy={sortBy}
                        sortType={sortType}
                        setSortBy={setSortBy}
                        setSortType={setSortType}
                    ></ProfitSummaryList>

                </Box>
            </Box>
            <Loading show={loading} type='fullLoading' />


            <Box
                id="print-table-container"
                ref={componentRef}
            >
                <Typography fontSize="14px" fontWeight={400}>{t('REPORT.REPORT_NAME.PROFIT_SUMMARY')}</Typography>
                <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
                    {getAllFilter().map((e: any, index: number) => (
                        <Typography key={index} fontSize="10px" fontWeight={400}>
                            {`${e.label}: ${e.value}`}
                        </Typography>
                    ))}
                </Box>

                <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
                    <thead>
                        <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700, width: '50px' }}>
                            <th style={{ borderBottom: '1px solid black', width: '50px' }}>{t('REPORT.TABLE.PROFIT_SUMMARY.NO')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left', width: '85px' }}>{t('REPORT.TABLE.PROFIT_SUMMARY.TREATMENT_CODE')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.PROFIT_SUMMARY.TREATMENT_NAME')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center', width: '85px' }}>{t('REPORT.TABLE.PROFIT_SUMMARY.COST_PER_SESSION')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center', width: '85px' }}>{t('REPORT.TABLE.PROFIT_SUMMARY.PRICE_PER_SESSION')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center', width: '85px' }}>{t('REPORT.TABLE.PROFIT_SUMMARY.QUANTITY')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center', width: '85px' }}>{t('REPORT.TABLE.PROFIT_SUMMARY.TOTAL_COST')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center', width: '85px' }}>{t('REPORT.TABLE.PROFIT_SUMMARY.TOTAL_PAID')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center', width: '85px' }}>{t('REPORT.TABLE.PROFIT_SUMMARY.TOTAL_PAYMENT')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center', width: '85px' }}>{t('REPORT.TABLE.PROFIT_SUMMARY.TOTAL_PROFIT')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.PROFIT_SUMMARY.NET_PROFIT')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {profitSummaryExportData?.length === 0 &&
                            <tr>
                                <td colSpan={9} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100%' }}>{t('NOT_FOUND')}</td>
                            </tr>
                        }
                        {profitSummaryExportData.map((data: any, index: number) => (
                            <tr key={index}>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{index + 1}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '85px' }}>{data?.operativeCode || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{data?.operativeName || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: '85px' }}>{numberFormat(data?.costPerUnit || 0)}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: '85px' }}>{numberFormat(data?.listPricePerUnit || 0)}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '85px' }}>{Number(data?.qty) || 0}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: '85px' }}>{numberFormat(data?.totalCost || 0)}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: '85px' }}>{numberFormat(data?.totalPaid || 0)}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: '85px' }}>{numberFormat(data?.totalListPrice || 0)}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: '85px' }}>{numberFormat(data?.grossProfit || 0)}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center' }}>{numberFormat(data?.netProfit || 0)}</td>
                            </tr>
                        ))}

                        <tr>
                            <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{''}</td>
                            <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '85px' }}>{''}</td>
                            <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center' }}>{t('REPORT.TITLE.SUM')}</td>
                            <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: '85px' }}>{numberFormat(_.sumBy(profitSummaryExportData, (item: any) => Number(item.costPerUnit)) || 0)}</td>
                            <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: '85px' }}>{numberFormat(_.sumBy(profitSummaryExportData, (item: any) => Number(item.listPricePerUnit)) || 0)}</td>
                            <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '85px' }}>{_.sumBy(profitSummaryExportData, (item: any) => Number(item.qty)) || 0}</td>
                            <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: '85px' }}>{numberFormat(_.sumBy(profitSummaryExportData, (item: any) => Number(item.totalCost)) || 0)}</td>
                            <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: '85px' }}>{numberFormat(_.sumBy(profitSummaryExportData, (item: any) => Number(item.totalPaid)) || 0)}</td>
                            <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: '85px' }}>{numberFormat(_.sumBy(profitSummaryExportData, (item: any) => Number(item.totalListPrice)) || 0)}</td>
                            <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center', width: '85px' }}>{numberFormat(_.sumBy(profitSummaryExportData, (item: any) => Number(item.grossProfit)) || 0)}</td>
                            <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center' }}>{numberFormat(_.sumBy(profitSummaryExportData, (item: any) => Number(item.netProfit)) || 0)}</td>
                        </tr>
                    </tbody>
                </table>
            </Box>
        </Box >
    )
}